import type { TaskActivity, TaskActivityTypeT } from '@portfolio-pm/shared';
import { newId } from '@portfolio-pm/shared';
import * as Y from 'yjs';
import { type WithNow, getTaskActivitiesMap } from './doc.ts';

export interface CreateTaskActivityInput {
  taskId: string;
  type: TaskActivityTypeT;
  message: string;
}

function ts(options: WithNow): number {
  return options.now ?? Date.now();
}

export function listTaskActivities(doc: Y.Doc): TaskActivity[] {
  const out: TaskActivity[] = [];
  for (const [id, inner] of getTaskActivitiesMap(doc).entries()) {
    out.push(taskActivityFromMap(id, inner));
  }
  return out.sort((a, b) => b.createdAt - a.createdAt);
}

export function getTaskActivity(doc: Y.Doc, id: string): TaskActivity | undefined {
  const inner = getTaskActivitiesMap(doc).get(id);
  return inner ? taskActivityFromMap(id, inner) : undefined;
}

export function taskActivitiesForTask(doc: Y.Doc, taskId: string): TaskActivity[] {
  return listTaskActivities(doc).filter((activity) => activity.taskId === taskId);
}

export function createTaskActivity(
  doc: Y.Doc,
  input: CreateTaskActivityInput,
  options: WithNow = {},
): TaskActivity {
  const now = ts(options);
  const id = newId();
  const inner = new Y.Map<unknown>();
  inner.set('taskId', input.taskId);
  inner.set('type', input.type);
  inner.set('message', input.message);
  inner.set('createdAt', now);
  getTaskActivitiesMap(doc).set(id, inner);
  return taskActivityFromMap(id, inner);
}

export function deleteTaskActivity(doc: Y.Doc, id: string): void {
  getTaskActivitiesMap(doc).delete(id);
}

export function updateTaskActivity(doc: Y.Doc, id: string, message: string): void {
  const inner = getTaskActivitiesMap(doc).get(id);
  if (inner) inner.set('message', message.trim());
}

function taskActivityFromMap(id: string, inner: Y.Map<unknown>): TaskActivity {
  return {
    id,
    taskId: String(inner.get('taskId') ?? ''),
    type: String(inner.get('type') ?? 'comment') as TaskActivityTypeT,
    message: String(inner.get('message') ?? ''),
    createdAt: Number(inner.get('createdAt') ?? 0),
  };
}
